import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  MagnifyingGlass, ArrowClockwise, DownloadSimple, PaperPlaneTilt,
  CircleNotch, FilePdf, EnvelopeSimple, CheckCircle,
} from '@phosphor-icons/react';
import { API } from '@/config/api';

// Renders the "Invoices" tab on Admin Dashboard.
// - Table of every previously-sent Commission Invoice
// - Search by invoice #, recipient email, or billed-to company
// - Re-download (opens PDF in a new tab)
// - Re-send (opens editable email overrides + fires POST resend)
export default function InvoiceHistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resendTarget, setResendTarget] = useState(null); // full invoice doc
  const [resendForm, setResendForm] = useState({ recipient_email: '', email_subject: '', email_body: '' });
  const [resending, setResending] = useState(false);
  const [markingId, setMarkingId] = useState('');

  const handleMarkReceived = async (inv) => {
    const label = inv.tranche === 2 ? '2/2' : '1/2';
    if (!window.confirm(`Confirm the college has paid the ${label} commission on Invoice #${inv.invoice_number}? This marks ${(inv.line_items || []).length} student(s) as commission received.`)) return;
    setMarkingId(inv.invoice_number);
    try {
      const res = await axios.post(`${API}/admin/invoices/${inv.invoice_number}/mark-commission-received`);
      toast.success(`Marked ${res.data?.students_updated || 0} student(s) — ${label} commission received`);
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark commission received');
    } finally {
      setMarkingId('');
    }
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices`, {
        params: search.trim() ? { search: search.trim() } : {},
      });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const totalGrand = useMemo(
    () => items.reduce((sum, inv) => sum + (Number(inv.grand_total_commission) || 0), 0),
    [items],
  );

  const handleDownload = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.invoice_number}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab so the admin can quickly review or print
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a short delay so the tab has time to load the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const openResend = (inv) => {
    setResendTarget(inv);
    setResendForm({
      recipient_email: inv.recipient_email || '',
      email_subject: inv.email_subject || `Study Alliance — Invoice #${inv.invoice_number}`,
      email_body: inv.email_body || `Please find attached invoice #${inv.invoice_number}.`,
    });
  };

  const submitResend = async () => {
    if (!resendTarget) return;
    if (!resendForm.recipient_email.trim()) { toast.error('Recipient email is required'); return; }
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${resendTarget.invoice_number}/resend`,
        resendForm,
      );
      toast.success(`Invoice #${resendTarget.invoice_number} resent to ${res.data?.resent_to}`);
      setResendTarget(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="admin-invoices-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Commission Invoice History</h2>
          <p className="text-xs text-gray-500 mt-0.5">Every invoice sent to a partner college — re-download the PDF or re-send the email at any time.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loading} data-testid="invoices-refresh-btn">
          {loading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <ArrowClockwise size={14} className="mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="invoices-stat-count">
          <p className="text-xs text-gray-500 font-medium">Invoices Sent</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{items.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="invoices-stat-total">
          <p className="text-xs text-gray-500 font-medium">Total Commission Billed</p>
          <p className="text-xl font-bold text-emerald-700 mt-1">${totalGrand.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="invoices-stat-latest">
          <p className="text-xs text-gray-500 font-medium">Latest Invoice</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{items[0]?.invoice_number ? `#${items[0].invoice_number}` : '—'}</p>
          {items[0]?.sent_at && <p className="text-xs text-gray-400 mt-0.5">{new Date(items[0].sent_at).toLocaleString()}</p>}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-gray-400 ml-1" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by invoice #, recipient email, or company name…"
          className="border-0 shadow-none focus-visible:ring-0 h-9"
          data-testid="invoices-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tranche</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Recipient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Billed To</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Students</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Commission</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm"><CircleNotch size={18} className="inline animate-spin mr-2" /> Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">
                  No invoices sent yet. Once you fire an &quot;Invoice Action&quot; from the Students tab, they&apos;ll appear here.
                </td></tr>
              )}
              {items.map((inv) => {
                const tranche = Number(inv.tranche) === 2 ? 2 : 1;
                const trancheLabel = tranche === 2 ? '2/2' : '1/2';
                const trancheColor = tranche === 2 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-lime-100 text-lime-800 border-lime-300';
                const commissionReceived = !!inv.commission_received;
                return (
                <tr key={inv.invoice_number} className="hover:bg-gray-50/50" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">#{inv.invoice_number}</p>
                    {inv.last_resent_at && (
                      <p className="text-[10px] text-amber-600 mt-0.5" title={`Last resent ${new Date(inv.last_resent_at).toLocaleString()}`}>
                        Resent {new Date(inv.last_resent_at).toLocaleDateString()}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${trancheColor}`} data-testid={`invoice-tranche-${inv.invoice_number}`}>
                      {trancheLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{inv.invoice_date || (inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : '—')}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900 truncate max-w-[220px]">{inv.recipient_email || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell truncate max-w-[200px]">
                    {inv.billed_to?.company_name || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{(inv.line_items || []).length}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-emerald-700">
                      ${Number(inv.grand_total_commission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(inv)}
                        className="h-8 px-2 text-xs"
                        data-testid={`invoice-download-${inv.invoice_number}`}
                        title="Open the invoice PDF in a new tab"
                      >
                        <DownloadSimple size={14} className="mr-1" /> PDF
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openResend(inv)}
                        className="h-8 px-2 text-xs bg-blue-700 hover:bg-blue-800 text-white"
                        data-testid={`invoice-resend-${inv.invoice_number}`}
                        title="Re-send this invoice by email"
                      >
                        <EnvelopeSimple size={14} className="mr-1" /> Resend
                      </Button>
                      {commissionReceived ? (
                        <span className="inline-flex items-center gap-1 h-8 px-2 text-xs font-semibold rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`invoice-received-badge-${inv.invoice_number}`} title={inv.commission_received_at ? `Marked ${new Date(inv.commission_received_at).toLocaleDateString()}` : ''}>
                          <CheckCircle size={14} weight="fill" /> {trancheLabel} Received
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleMarkReceived(inv)}
                          disabled={markingId === inv.invoice_number}
                          className="h-8 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`invoice-mark-received-${inv.invoice_number}`}
                          title={`Confirm the college paid the ${trancheLabel} commission`}
                        >
                          {markingId === inv.invoice_number
                            ? <CircleNotch size={14} className="mr-1 animate-spin" />
                            : <CheckCircle size={14} className="mr-1" />}
                          {trancheLabel} Received
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resend dialog */}
      <Dialog open={!!resendTarget} onOpenChange={(o) => { if (!o) setResendTarget(null); }}>
        <DialogContent className="max-w-xl" data-testid="invoice-resend-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Re-send Invoice #{resendTarget?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              The original PDF is re-attached. Blank fields fall back to the values used when the invoice was first sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Recipient Email</Label>
              <Input
                type="email"
                value={resendForm.recipient_email}
                onChange={(e) => setResendForm(p => ({ ...p, recipient_email: e.target.value }))}
                data-testid="invoice-resend-recipient"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input
                value={resendForm.email_subject}
                onChange={(e) => setResendForm(p => ({ ...p, email_subject: e.target.value }))}
                data-testid="invoice-resend-subject"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email Body</Label>
              <Textarea
                rows={6}
                value={resendForm.email_body}
                onChange={(e) => setResendForm(p => ({ ...p, email_body: e.target.value }))}
                className="font-mono text-xs"
                data-testid="invoice-resend-body"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-xs text-blue-900 flex items-center gap-2">
              <FilePdf size={16} className="text-blue-700 shrink-0" />
              StudyAlliance_Invoice_{resendTarget?.invoice_number}.pdf will be attached automatically.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResendTarget(null)} disabled={resending} data-testid="invoice-resend-cancel">Cancel</Button>
              <Button onClick={submitResend} disabled={resending} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="invoice-resend-submit">
                {resending ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Sending…</> : <><PaperPlaneTilt size={14} className="mr-1" /> Resend Now</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
